import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo-data";
import { Calendar, ChevronRight, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

const CATEGORY_COLORS = {
  "Guide": "bg-blue-100 text-blue-700",
  "Conseils": "bg-green-100 text-green-700",
  "Que faire ?": "bg-orange-100 text-orange-700",
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/articles`)
      .then(r => { setPosts(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Blog — Guides et conseils pour acheter en ligne sereinement"
        description="Articles pratiques, guides et conseils pour reconnaître les sites fiables, éviter les arnaques et acheter en ligne en toute sécurité."
        path="/blog"
        type="website"
        keywords="blog arnaque internet, guide achat en ligne, sécurité e-commerce, conseils consommateur"
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "Blog", path: "/blog" },
        ])}
      />
      <Header />
      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-4xl">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">Blog</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 tracking-tight mt-3 mb-4">
              Guides et conseils
            </h1>
            <p className="text-slate-600 text-base max-w-xl mx-auto leading-relaxed">
              Informations pratiques pour acheter plus sereinement en ligne et reconnaître les signaux d'alerte.
            </p>
          </div>

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="card-base p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-1/4 mb-4" />
                  <div className="h-6 bg-slate-200 rounded mb-3" />
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-16 text-slate-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun article pour le moment. Revenez bientôt !</p>
            </div>
          )}

          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {posts.map(post => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="card-base p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[post.category] || "bg-slate-100 text-slate-600"}`}>
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </span>
                  </div>
                  <h2 className="font-bold text-slate-800 mb-2 font-heading group-hover:text-[#1E3A8A] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-1 text-sm font-semibold text-[#1E3A8A]">
                    Lire l'article <ChevronRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
